import {
  type ButtonHTMLAttributes,
  forwardRef,
  type PropsWithChildren,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavLink,
  type NavLinkProps,
  type NavLinkRenderProps,
} from 'react-router-dom';
import WatchIcon from '@assets/img/icons/watch.svg?react';
import { H2, H3, StringMain } from '@components/Typography/Typography';
import { Loader } from '@components/ui/Loader/Loader';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'classnames';
import { motion } from 'framer-motion';

import css from './styles.module.scss';

import watchADSvip from '/img/VIP/vip-icon.png';
import watchADS from '/img/watch.png';

const buttonVariants = cva(
  'outline-none flex items-center justify-center rounded-lg w-full',
  {
    variants: {
      variant: {
        primary: css.primary,
        blue: css.blue,
        blueSm: css.blueSm,
        purple: css.purple,
        coral: css.coral,
        purpleSm: css.purpleSm,
        primarySm: css.primarySm,
        secondary: css.secondary,
        secondarySm: css.secondarySm,
        red: css.red,
        'dark-red': css.darkRed,
        transparent: 'bg-transparent border-none',
        'grey-blur': css['grey-blur'],
        'grey-blur-sm': css['grey-blur-sm'],
        disabled: css.disabled,
        rounded: css.rounded,
        greyRounded: css.greyRounded,
        bordered: css.bordered,
        borderedSm: css.borderedSm,
        yellow: css.yellow,
        watchSecondary: css.watchSecondary,
      },
    },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    PropsWithChildren {
  inactive?: boolean;
  disablePressEffect?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant, inactive, disablePressEffect, ...props }, ref) => (
    <button
      ref={ref}
      {...props}
      className={cn(
        buttonVariants({ variant }),
        !disablePressEffect &&
          'active:scale-95 transition-transform duration-150 ease-in-out',
        props.className,
        inactive && buttonVariants({ variant: 'disabled' }),
      )}
    >
      {children}
    </button>
  ),
);

const MotionButton = motion.create(Button);

interface MainButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    PropsWithChildren {
  text: string;
  isLoading?: boolean;
  inactive?: boolean;
  classNamesText?: string;
}

const MainButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="primary">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <H2
            className={cn(
              !props.inactive && 'main-btn-text-shadow !text-[18px]',
            )}
          >
            {text}
          </H2>
          {children}
        </>
      )}
    </Button>
  ),
);

const DarkRedButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, isLoading, text, ...props }, ref) => (
    <Button ref={ref} {...props} variant="dark-red">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <H3 weight="bold" className="!text-red">
            {text}
          </H3>
          {children}
        </>
      )}
    </Button>
  ),
);

const RedButton = forwardRef<HTMLButtonElement, Omit<MainButtonProps, 'text'>>(
  ({ children, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="red">
      {isLoading ? <Loader /> : children}
    </Button>
  ),
);

const MotionRedButtton = motion.create(RedButton);

const BlueButton = forwardRef<HTMLButtonElement, Omit<MainButtonProps, 'text'>>(
  ({ children, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="blue">
      {isLoading ? <Loader /> : children}
    </Button>
  ),
);

const MotionBlueButton = motion.create(BlueButton);

const BlueSmButton = forwardRef<
  HTMLButtonElement,
  Omit<MainButtonProps, 'text'>
>(({ children, isLoading, ...props }, ref) => (
  <Button ref={ref} {...props} variant="blueSm">
    {isLoading ? <Loader /> : children}
  </Button>
));

const PurpleButton = forwardRef<
  HTMLButtonElement,
  Omit<MainButtonProps, 'text'>
>(({ children, isLoading, ...props }, ref) => (
  <Button ref={ref} {...props} variant="purple">
    {isLoading ? <Loader /> : children}
  </Button>
));

const CoralButton = forwardRef<
  HTMLButtonElement,
  Omit<MainButtonProps, 'text'>
>(({ children, isLoading, ...props }, ref) => (
  <Button ref={ref} {...props} variant="coral">
    {isLoading ? <Loader /> : children}
  </Button>
));

const MotionPurpleButton = motion.create(PurpleButton);

const PurpleSmButton = forwardRef<
  HTMLButtonElement,
  Omit<MainButtonProps, 'text'>
>(({ children, isLoading, ...props }, ref) => (
  <Button ref={ref} {...props} variant="purpleSm">
    {isLoading ? <Loader /> : children}
  </Button>
));

const MainSmButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="primarySm">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <StringMain
            className={cn(!props.inactive && 'main-btn-text-shadow')}
            weight="bold"
          >
            {text}
          </StringMain>
          {children}
        </>
      )}
    </Button>
  ),
);

const SecondaryButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="secondary">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <H2 className="main-btn-text-shadow">{text}</H2>
          {children}
        </>
      )}
    </Button>
  ),
);

const SecondarySmButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="secondarySm">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <StringMain weight="bold" className="main-btn-text-shadow">
            {text}
          </StringMain>
          {children}
        </>
      )}
    </Button>
  ),
);

const BorderedButton = forwardRef<
  HTMLButtonElement,
  Omit<MainButtonProps, 'text'>
>(({ children, isLoading, ...props }, ref) => (
  <Button ref={ref} {...props} variant="bordered">
    {isLoading ? <Loader /> : children}
  </Button>
));

const BorderedSmButton = forwardRef<
  HTMLButtonElement,
  Omit<MainButtonProps, 'text'>
>(({ children, isLoading, ...props }, ref) => (
  <Button ref={ref} {...props} variant="borderedSm">
    {isLoading ? <Loader /> : children}
  </Button>
));

interface WathcButtonProps extends ButtonProps {
  text?: string;
  endText?: string;
  additionText?: string;
  isVIP?: boolean;
  isIconVisible?: boolean;
  isLoading?: boolean;
}

const WatchButton = forwardRef<HTMLButtonElement, WathcButtonProps>(
  ({ endText, text, isLoading, isVIP, ...props }, ref) => {
    const { t } = useTranslation();

    return (
      <Button
        ref={ref}
        {...props}
        className={cn(
          props.className,
          'flex items-center gap-2 backdrop-blur-[32px]',
        )}
        variant="secondary"
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <img src={isVIP ? watchADSvip : watchADS} alt="" width={24} />
            <StringMain weight="extrabold" className="watch-btn-text">
              {isVIP ? (text ? t(text) : 'Free') : text ? t(text) : 'Watch ADS'}{' '}
              {props.additionText && <span>{props.additionText}</span>}
            </StringMain>
          </>
        )}
      </Button>
    );
  },
);

const WatchSecondaryButton = forwardRef<HTMLButtonElement, WathcButtonProps>(
  ({ endText, text, isLoading, ...props }, ref) => {
    const { t } = useTranslation();

    return (
      <Button
        ref={ref}
        {...props}
        className={cn(props.className, 'flex items-center gap-1')}
        variant="watchSecondary"
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <StringMain weight="extrabold" className="!text-gold">
              {props.isVIP ? t('Free') : text ? t(text) : 'Watch ADS'}
            </StringMain>
            {!props.isVIP && <WatchIcon className="w-[24px]" />}
            {props.additionText && (
              <StringMain weight="extrabold" className="!text-gold">
                {props.additionText}
              </StringMain>
            )}
          </>
        )}
      </Button>
    );
  },
);

const WatchSmButton = forwardRef<HTMLButtonElement, WathcButtonProps>(
  ({ endText, text, isLoading, isIconVisible, isVIP, ...props }, ref) => {
    const { t } = useTranslation();

    return (
      <Button
        ref={ref}
        {...props}
        className={cn(
          props.className,
          'flex items-center gap-2 backdrop-blur-[32px] h-8',
        )}
        variant="secondary"
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {!isIconVisible && (
              <img src={isVIP ? watchADSvip : watchADS} alt="" width={24} />
            )}
            <StringMain weight="bold" className="xl:text-sm">
              {isVIP ? (text ? t(text) : 'Free') : text ? t(text) : 'ADS'}{' '}
            </StringMain>
          </>
        )}
      </Button>
    );
  },
);

const GreyBlurButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="grey-blur">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <span className="font-extrabold text-base white-text-shadow-gradient leading-none">
            {text}
          </span>
          {children}
        </>
      )}
    </Button>
  ),
);

const GreyBlurSmButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, classNamesText, ...props }, ref) => (
    <Button ref={ref} {...props} variant="grey-blur-sm">
      {isLoading ? (
        <Loader />
      ) : (
        <div className={cn('flex', classNamesText)}>
          <span className="text-[0.75rem] font-[700] white-text-shadow-gradient leading-none">
            {text}
          </span>
          {children}
        </div>
      )}
    </Button>
  ),
);
const RoundedButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, classNamesText, ...props }, ref) => (
    <Button ref={ref} {...props} variant="rounded">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <span
            className={cn(
              classNamesText,
              'font-extrabold text-base white-text-shadow-gradient leading-none',
            )}
          >
            {text}
          </span>
          {children}
        </>
      )}
    </Button>
  ),
);

const GreyRoundedButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, classNamesText, ...props }, ref) => (
    <Button ref={ref} {...props} variant="greyRounded">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <span
            className={cn(
              classNamesText,
              'font-extrabold text-base white-text-shadow-gradient leading-none',
            )}
          >
            {text}
          </span>
          {children}
        </>
      )}
    </Button>
  ),
);

interface LinkProps extends NavLinkProps, VariantProps<typeof buttonVariants> {}

const LinkButton = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ children, variant, ...props }, ref) => (
    <NavLink
      ref={ref}
      {...props}
      className={cn(buttonVariants({ variant }), props.className)}
    >
      {children}
    </NavLink>
  ),
);

interface MainLinkButtonProps extends NavLinkProps {
  text: string;
  navLinkRenderProps?: NavLinkRenderProps;
}

const MainLinkButton = forwardRef<HTMLAnchorElement, MainLinkButtonProps>(
  (
    {
      children,
      text,
      navLinkRenderProps = {
        isActive: false,
        isPending: false,
        isTransitioning: false,
      },
      ...props
    },
    ref,
  ) => (
    <LinkButton ref={ref} {...props} variant="primary">
      <H2>{text}</H2>
      {typeof children === 'function' ? children(navLinkRenderProps) : children}
    </LinkButton>
  ),
);

const YellowButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  ({ children, text, isLoading, ...props }, ref) => (
    <Button ref={ref} {...props} variant="yellow">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <H2 className={cn(!props.inactive && 'main-btn-text-shadow')}>
            {text}
          </H2>
          {children}
        </>
      )}
    </Button>
  ),
);

export {
  BlueButton,
  BlueSmButton,
  BorderedButton,
  BorderedSmButton,
  Button,
  CoralButton,
  DarkRedButton,
  GreyBlurButton,
  GreyBlurSmButton,
  GreyRoundedButton,
  LinkButton,
  MainButton,
  type MainButtonProps,
  MainLinkButton,
  MainSmButton,
  MotionBlueButton,
  MotionButton,
  MotionPurpleButton,
  MotionRedButtton,
  PurpleButton,
  PurpleSmButton,
  RedButton,
  RoundedButton,
  SecondaryButton,
  SecondarySmButton,
  WatchButton,
  WatchSecondaryButton,
  WatchSmButton,
  YellowButton,
};
